// ─────────────────────────────────────────────
// src/App.jsx
// Root component. Manages global state (current
// page, logged-in user, logged-in staff user)
// and renders the correct page.
// ─────────────────────────────────────────────
import { useState, useEffect } from "react";
import Nav                     from "./components/Nav";
import HomePage                from "./pages/HomePage";
import AboutPage               from "./pages/AboutPage";
import ProgramsPage            from "./pages/ProgramsPage";
import AfterSchoolProgramsPage from "./pages/AfterSchoolProgramsPage";
import FairfaxPage             from "./pages/FairfaxPage";
import FAQPage                 from "./pages/FAQPage";
import TeachPage               from "./pages/TeachPage";
import LoginPage               from "./pages/LoginPage";
import DashboardPage           from "./pages/DashboardPage";
import StaffLoginPage          from "./pages/StaffLoginPage";
import StaffPanel              from "./pages/StaffPanel";
import ChatBot                 from "./components/ChatBot";
import { supabase }            from "./lib/supabase";

// Real, crawlable URL for every page key. Keeping this as a plain map (rather
// than pulling in a router library) preserves the existing setPage()-driven
// navigation while giving every page its own address for search engines,
// social previews, and deep links.
const PAGE_PATHS = {
  "home":                   "/",
  "about":                  "/about",
  "programs":               "/programs",
  "after-school-programs":  "/after-school-programs",
  "fairfax-va":             "/fairfax-va",
  "faq":                    "/faq",
  "teach":                  "/teach",
  "login":                  "/login",
  "dashboard":              "/dashboard",
  "staff-login":            "/staff-login",
  "staff":                  "/staff",
};

function pathForPage(pageKey) {
  const [base, hash] = pageKey.split("#");
  const path = PAGE_PATHS[base] || "/";
  return hash ? `${path}#${hash}` : path;
}

function pageForPath(pathname) {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = Object.entries(PAGE_PATHS).find(([, p]) => p === normalized);
  return match ? match[0] : "home";
}

export default function App() {
  const [page,      setPageState] = useState(() => pageForPath(window.location.pathname));
  const [user,      setUser]      = useState(null);   // logged-in parent
  const [staffUser, setStaffUser] = useState(null);   // logged-in staff (admin/teacher)
  const [authReady, setAuthReady] = useState(false);

  // Restore Supabase session on load and listen for sign-out
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        // Check staff_profiles first — if found, this is a staff session
        const { data: staffProfile } = await supabase
          .from("staff_profiles")
          .select("*")
          .eq("id", session.user.id)
          .single();

        if (staffProfile) {
          if (staffProfile.active === false) {
            await supabase.auth.signOut();
          } else {
            setStaffUser(staffProfile);
          }
        } else {
          // Fall back to parent profile
          const { data: profile } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", session.user.id)
            .single();
          if (profile) setUser({ ...profile, students: [] });
        }
      }
      setAuthReady(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setUser(null);
        setStaffUser(null);
        setPageState("home");
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  function setPage(newPage) {
    window.history.pushState({ page: newPage }, "", pathForPage(newPage));
    setPageState(newPage);
  }

  // Handle browser back/forward buttons, and normalize the URL for
  // whichever page we booted into (e.g. a direct visit to /programs).
  useEffect(() => {
    const initial = pageForPath(window.location.pathname);
    window.history.replaceState({ page: initial }, "", pathForPage(initial));
    function onPopState(e) {
      const p = e.state?.page ?? pageForPath(window.location.pathname);
      setPageState(p);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Scroll to top on every page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  // Per-page title, meta description, canonical URL, Open Graph tags,
  // robots tag, and breadcrumb structured data.
  useEffect(() => {
    const SEO = {
      "home":                  { title: "Mars Academy | STEM & Coding Programs in Fairfax, VA",           desc: "Mars Academy offers hands-on STEM and coding programs before and after school for elementary and middle school students in Fairfax County, VA and Northern Virginia.", robots: "index, follow", label: "Home" },
      "about":                 { title: "About Mars Academy | STEM Education in Fairfax, VA",             desc: "Mars Academy brings IDCA-certified STEM education in coding, AI, and digital skills to elementary and middle school students across Fairfax County, Virginia.", robots: "index, follow", label: "About" },
      "programs":              { title: "STEM & Coding Programs for Elementary Students | Mars Academy",  desc: "Mars Academy's hands-on STEM program for Fairfax County students covers coding with Scratch, AI, internet safety, cloud computing, and digital infrastructure.", robots: "index, follow", label: "Programs" },
      "after-school-programs": { title: "STEM Before & After School Programs in Fairfax, VA | Mars Academy", desc: "Mars Academy's STEM program runs before and after school, bringing hands-on coding, AI, and technology to elementary students across Fairfax County, VA.", robots: "index, follow", label: "Before & After School Programs" },
      "fairfax-va":             { title: "STEM Programs for Kids in Fairfax, VA | Mars Academy",           desc: "Mars Academy partners with schools across Fairfax County, VA to bring hands-on STEM, coding, and technology programs to elementary and middle school students.", robots: "index, follow", label: "Fairfax, VA" },
      "faq":                   { title: "Mars Academy FAQ | STEM Programs in Fairfax, VA",                 desc: "Answers to common questions about Mars Academy's STEM program in Fairfax, VA, including registration, scholarships, instructors, and school partnerships.", robots: "index, follow", label: "FAQ" },
      "teach":                 { title: "Teach STEM at Mars Academy | Instructor Jobs in Fairfax, VA",     desc: "Join Mars Academy as a STEM instructor teaching hands-on coding and technology programs to elementary and middle school students in Fairfax County, Virginia.", robots: "index, follow", label: "Teach With Us" },
      "login":                 { title: "Parent Portal | Mars Academy",                                    desc: "Log in to your Mars Academy parent account to enroll your child in STEM programs and manage your family's schedule.", robots: "noindex, nofollow" },
      "dashboard":             { title: "My Dashboard | Mars Academy",                                     desc: "Manage your child's enrollment, schedule, and progress at Mars Academy.", robots: "noindex, nofollow" },
      "staff-login":           { title: "Staff Login | Mars Academy",                                      desc: "Mars Academy staff portal.", robots: "noindex, nofollow" },
      "staff":                 { title: "Staff Panel | Mars Academy",                                      desc: "Mars Academy internal staff panel.", robots: "noindex, nofollow" },
    };
    const base = page.split("#")[0];
    const { title, desc, robots, label } = SEO[base] || SEO.home;
    const canonicalUrl = `https://www.marsacademy.ai${pathForPage(base)}`;

    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", desc);
    document.querySelector('meta[name="robots"]')?.setAttribute("content", robots);
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", canonicalUrl);
    document.querySelector('meta[property="og:url"]')?.setAttribute("content", canonicalUrl);
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", title);
    document.querySelector('meta[property="og:description"]')?.setAttribute("content", desc);
    document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", title);
    document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", desc);

    // Breadcrumb structured data for every indexable, non-home page.
    document.getElementById("__breadcrumb-schema")?.remove();
    if (robots === "index, follow" && base !== "home") {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "__breadcrumb-schema";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.marsacademy.ai/" },
          { "@type": "ListItem", "position": 2, "name": label, "item": canonicalUrl },
        ],
      });
      document.head.appendChild(script);
    }
  }, [page]);

  if (!authReady) return (
    <div style={{ background: "#080B26", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: "'Fredoka One',cursive", color: "rgba(255,255,255,0.3)", fontSize: "1.2rem" }}>🚀 Loading…</div>
    </div>
  );

  return (
    <div style={{ fontFamily: "'Nunito', sans-serif", background: "#080B26", color: "#fff", minHeight: "100vh" }}>

      {/* ── Global CSS ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@400;600;700;800&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #080B26; }

        /* Keyframe animations used across pages */
        @keyframes twinkle   { 0%,100%{opacity:0.15} 50%{opacity:0.85} }
        @keyframes float1    { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-15px) rotate(5deg)} }
        @keyframes float2    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-11px)} }
        @keyframes float3    { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-19px) rotate(-6deg)} }
        @keyframes rocketBob { 0%,100%{transform:translateY(0) rotate(-25deg)} 50%{transform:translateY(-12px) rotate(-25deg)} }
        @keyframes fadeUp       { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
        @keyframes splineFadeIn { from{opacity:0} to{opacity:0.3} }

        /* Hide Spline watermark */
        #logo,
        a[href*="spline.design"],
        [class*="spline-watermark"],
        canvas ~ a,
        canvas ~ div a { display: none !important; }

        /* Placeholder text colour */
        input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.26); }

        /* Native select option background */
        select option { background: #0e1240; color: #fff; }

        /* Thin custom scrollbar */
        ::-webkit-scrollbar       { width: 5px; }
        ::-webkit-scrollbar-track { background: #080B26; }
        ::-webkit-scrollbar-thumb { background: rgba(255,217,61,0.28); border-radius: 3px; }

        /* Responsive nav — desktop links visible by default */
        .nav-desktop   { display: flex !important; }
        .nav-hamburger { display: none !important; }

        @media (max-width: 768px) {
          .nav-desktop   { display: none !important; }
          .nav-hamburger { display: block !important; }
        }

        @media (max-width: 1024px) {
          .spline-bg { display: none !important; }
        }

        /* ── Admin panel mobile fixes ── */
        .admin-panel { padding: 4.5rem 0.75rem 2.5rem !important; }
        .admin-tabs { width: 100% !important; overflow-x: auto !important; -webkit-overflow-scrolling: touch; scrollbar-width: none; -ms-overflow-style: none; }
        .admin-tabs::-webkit-scrollbar { display: none; }
        .staff-login { padding: 5rem 1rem 3rem !important; }
        @media (max-width: 600px) {
          /* ── Admin panel ── */
          .admin-tabs { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; }
          .admin-tabs button { padding: 8px 4px !important; font-size: 0.72rem !important; text-align: center !important; white-space: normal !important; line-height: 1.2 !important; }
          .roster-info-grid { grid-template-columns: 1fr !important; }
          .att-week-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .admin-filter-select { min-width: 0 !important; width: 100% !important; }
          .parent-header-row { flex-direction: column !important; align-items: stretch !important; gap: 0.6rem !important; }
          .parent-filters-row { flex-direction: column !important; align-items: stretch !important; }
          .parent-filters-row select,
          .parent-filters-row > div { width: 100% !important; flex: none !important; }

          /* ── Parent dashboard ── */
          .dash-panel { padding: 4.5rem 0.85rem 2.5rem !important; }
          .dash-welcome { padding: 1.1rem 1.2rem !important; flex-direction: column !important; align-items: flex-start !important; gap: 0.75rem !important; }
          .dash-welcome-title { font-size: 1.3rem !important; }
          .dash-tabs { width: 100% !important; }
          .dash-tabs button { flex: 1 !important; padding: 8px 6px !important; font-size: 0.78rem !important; text-align: center !important; }
          .dash-schedule-row { grid-template-columns: auto 1fr !important; grid-template-rows: auto auto !important; }
          .dash-schedule-actions { grid-column: 1 / -1 !important; display: flex !important; justify-content: flex-end !important; }

          /* ── Founder section ── */
          .founder-section { padding: 3rem 1.25rem !important; }
          .founder-grid { text-align: center !important; gap: 2rem !important; }
          .founder-photo { width: 200px !important; height: 200px !important; max-width: 200px !important; }
          .founder-quote { text-align: left !important; }
        }

      `}</style>

      {/* ── Navigation ── */}
      <Nav
        page={page}
        setPage={setPage}
        user={user}
        setUser={setUser}
        staffUser={staffUser}
        setStaffUser={setStaffUser}
      />

      {/* ── Page routing ── */}
      {page === "home"                  && <HomePage                setPage={setPage} />}
      {page === "about"                 && <AboutPage                setPage={setPage} />}
      {page === "programs"              && <ProgramsPage             setPage={setPage} />}
      {page === "after-school-programs" && <AfterSchoolProgramsPage  setPage={setPage} />}
      {page === "fairfax-va"            && <FairfaxPage              setPage={setPage} />}
      {page === "faq"                   && <FAQPage                  setPage={setPage} />}
      {page === "teach"                 && <TeachPage                setPage={setPage} />}
      {page === "login"      && <LoginPage     setPage={setPage} setUser={setUser} />}
      {page === "dashboard"  && (user
        ? <DashboardPage user={user} setUser={setUser} setPage={setPage} />
        : <LoginPage     setPage={setPage} setUser={setUser} />
      )}
      {page === "staff-login" && <StaffLoginPage setPage={setPage} setStaffUser={setStaffUser} setUser={setUser} />}
      {page === "staff"       && (staffUser
        ? <StaffPanel     staffUser={staffUser} setPage={setPage} setStaffUser={setStaffUser} />
        : <StaffLoginPage setPage={setPage} setStaffUser={setStaffUser} setUser={setUser} />
      )}

      <ChatBot />

    </div>
  );
}
